import { useCallback, useEffect, useState } from "react";
import { apiClient } from "../lib/apiClient";
import { useAuth } from "./useAuth";

// useDisplayPreferences — per-user display toggles (#1465).
//
// Kept separate from useAlertPreferences: that hook manages a LIST of typed
// alert rows with plan-cap validation and an over-cap revert path. This is a
// flat object of cosmetic booleans with none of that. Merging them would put a
// display flag through the alert-cap error handling, which doesn't apply to it.

export interface DisplayPreferences {
  /**
   * When true, resolved properties (sold / redeemed / withdrawn /
   * auction_passed / delisted) appear in the default map and list. Default
   * false. Never affects search — a resolved property is always findable by
   * address, owner, or case number regardless of this setting.
   */
  showResolved: boolean;
}

const DEFAULTS: DisplayPreferences = { showResolved: false };

export function useDisplayPreferences() {
  const { user } = useAuth();
  // Depend on user.id, not the user object. Supabase's TOKEN_REFRESHED on
  // tab-return produces a new user object with the same id, and depending on
  // the object would re-fetch on every tab focus. Same reasoning as
  // useAlertPreferences and Dashboard's property effect.
  const userId = user?.id;

  const [prefs, setPrefs] = useState<DisplayPreferences>(DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) {
      setPrefs(DEFAULTS);
      return;
    }
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<DisplayPreferences>("/profile/display")
      .then(({ data }) => {
        if (!cancelled) setPrefs({ showResolved: Boolean(data?.showResolved) });
      })
      .catch(() => {
        // A failed read leaves the defaults in place rather than surfacing an
        // error: the panel is still usable, and the server is the authority on
        // what's actually being filtered either way.
        if (!cancelled) setPrefs(DEFAULTS);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  /**
   * Optimistic, with a real revert. The toggle flips immediately so it doesn't
   * feel laggy, but a failed PUT puts it back — leaving it flipped would show a
   * setting that isn't saved and won't survive a reload, which is worse than a
   * brief flicker. Same posture as useAlertPreferences' over-cap revert.
   *
   * Resolves true when the value was actually persisted, so the caller knows
   * whether refetching the property list is warranted.
   */
  const setShowResolved = useCallback(
    async (next: boolean): Promise<boolean> => {
      const previous = prefs.showResolved;
      setPrefs({ showResolved: next });
      setSaving(true);
      setError(null);
      try {
        await apiClient.put("/profile/display", { showResolved: next });
        return true;
      } catch (err: any) {
        setPrefs({ showResolved: previous });
        setError(err?.response?.data?.error || "Could not save that setting");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [prefs.showResolved],
  );

  return { ...prefs, loading, saving, error, setShowResolved };
}
